// Ore placement (GEN-7). Deterministic per-chunk vein scattering by the §13
// vertical distributions. A vein only replaces stone/deepslate, so an ore whose
// band is above the terrain (iron/emerald mountain bands) naturally appears only
// where stone reaches that high — no explicit biome test needed.
import { WORLD } from '../../config';
import { Rng } from '../../engine/rng';
import type { Chunk } from '../../engine/chunk';
import type { OreConfig } from '../../registry/schemas';

type DistEntry = OreConfig['dist'][number];

function triangularSample(min: number, peak: number, max: number, rng: Rng): number {
  const span = max - min;
  if (span <= 0) return min;
  const u = rng.nextFloat();
  const c = (peak - min) / span;
  const v = u < c ? min + Math.sqrt(u * span * (peak - min)) : max - Math.sqrt((1 - u) * span * (max - peak));
  return Math.round(v);
}

function sampleY(dist: readonly DistEntry[], rng: Rng): number {
  let total = 0;
  for (const d of dist) total += d.weight ?? 1;
  let r = rng.nextFloat() * total;
  let entry = dist[0]!;
  for (const d of dist) {
    r -= d.weight ?? 1;
    if (r <= 0) { entry = d; break; }
  }
  return entry.kind === 'uniform' ? entry.min + rng.nextInt(entry.max - entry.min + 1) : triangularSample(entry.min, entry.peak, entry.max, rng);
}

export class OrePlacer {
  private readonly resolved: Array<{ cfg: OreConfig; stone: number; deep: number; biomes: ReadonlySet<string> }>;

  constructor(
    private readonly baseRng: Rng,
    ores: readonly OreConfig[],
    resolve: (name: string) => number,
    private readonly stoneState: number,
    private readonly deepslateState: number,
    /** Surface biome at a column, for biome-gated ores. Absent ⇒ the gate is off
     *  and every ore generates everywhere its depth band allows (the behaviour
     *  before per-biome distributions). */
    private readonly biomeNameAt?: (x: number, z: number) => string,
  ) {
    this.resolved = ores.map((cfg) => ({
      cfg,
      stone: resolve(cfg.block),
      deep: cfg.deepslate ? resolve(cfg.deepslate) : resolve(cfg.block),
      biomes: new Set(cfg.biomes),
    }));
  }

  place(chunk: Chunk): void {
    const rng = this.baseRng.forkAt('ores', chunk.cx, chunk.cz);
    const bx = chunk.cx * WORLD.SECTION, bz = chunk.cz * WORLD.SECTION;
    for (const { cfg, stone, deep, biomes } of this.resolved) {
      for (let t = 0; t < cfg.tries; t++) {
        const y = sampleY(cfg.dist, rng);
        if (y < WORLD.MIN_Y + 1 || y > WORLD.MAX_Y) continue;
        const lx = rng.nextInt(16), lz = rng.nextInt(16);
        if (biomes.size > 0 && !biomes.has(this.biomeNameAt?.(bx + lx, bz + lz) ?? '')) continue;
        this.placeVein(chunk, lx, y, lz, cfg.veinSize, stone, deep, rng);
      }
    }
  }

  private placeVein(chunk: Chunk, cx: number, cy: number, cz: number, size: number, oreStone: number, oreDeep: number, rng: Rng): void {
    const r = Math.max(1, Math.cbrt(size));
    for (let i = 0; i < size; i++) {
      const lx = cx + Math.round((rng.nextFloat() * 2 - 1) * r);
      const y = cy + Math.round((rng.nextFloat() * 2 - 1) * r);
      const lz = cz + Math.round((rng.nextFloat() * 2 - 1) * r);
      if (lx < 0 || lx >= WORLD.SECTION || lz < 0 || lz >= WORLD.SECTION || y < WORLD.MIN_Y || y > WORLD.MAX_Y) continue;
      const s = chunk.getBlockState(lx, y, lz);
      if (s === this.stoneState) chunk.setBlockState(lx, y, lz, oreStone, alwaysBlock);
      else if (s === this.deepslateState) chunk.setBlockState(lx, y, lz, oreDeep, alwaysBlock);
    }
  }
}

const alwaysBlock = (): boolean => true;
